/**
 * Coffee Bean Clicker v2 - Producer & Tap Upgrade Panel
 * Scrollable list of tap upgrades + producers with buy buttons
 */

import * as PIXI from 'pixi.js';
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../../game/index.js';
import {
  getTapUpgradeCost, getProducerCost, applyCostDiscount,
  isProducerUnlocked, formatNumber,
} from '../../game/index.js';
import { TAP_UPGRADES, PRODUCERS } from '../../game/data/upgrades.js';
import { t } from '../../i18n/index.js';
import { THEME, LAYOUT } from './theme.js';
import {
  drawGlassCard, drawPillButton, drawIconBox, makeText,
} from './UIHelpers.js';
import { ScrollContainer } from './ScrollContainer.js';

const TAP_EMOJIS: Record<string, string> = {
  T1: '\uD83D\uDC46', T2: '\uD83E\uDDE4', T3: '\uD83D\uDD27', T4: '\u2699\uFE0F',
  T5: '\uD83E\uDD16', T6: '\uD83D\uDD2C', T7: '\u269B\uFE0F', T8: '\u23F3',
};

const PRODUCER_EMOJIS: Record<string, string> = {
  P1: '\uD83C\uDF31', P2: '\uD83C\uDF3F', P3: '\uD83C\uDF33', P4: '\uD83C\uDF34',
  P5: '\uD83C\uDFE1', P6: '\uD83C\uDF3E', P7: '\uD83D\uDD25', P8: '\uD83C\uDFED',
  P9: '\uD83D\uDEA2', P10: '\uD83C\uDFE2', P11: '\uD83D\uDC51', P12: '\uD83E\uDE90',
};

interface RowInfo {
  container: PIXI.Container;
  gfx: PIXI.Graphics;
  iconText: PIXI.Text;
  nameText: PIXI.Text;
  infoText: PIXI.Text;
  btnGfx: PIXI.Graphics;
  btnText: PIXI.Text;
  id: string;
  kind: 'tap' | 'producer';
}

export class ProducerPanel extends PIXI.Container {
  private core: GameCore<GameState, GameAction>;
  private scroll: ScrollContainer;
  private rows: RowInfo[] = [];
  private panelW: number;
  private sectionTapLabel!: PIXI.Text;
  private sectionProdLabel!: PIXI.Text;
  constructor(w: number, h: number, core: GameCore<GameState, GameAction>) {
    super();
    this.core = core;
    this.panelW = w;

    this.scroll = new ScrollContainer(w, h);
    this.addChild(this.scroll);

    this.buildLayout();
  }

  private buildLayout(): void {
    const content = this.scroll.content;
    const pad = LAYOUT.padding;
    const w = this.panelW;
    let y = pad;

    // Section: Tap Upgrades
    this.sectionTapLabel = makeText(
      `\uD83D\uDC46 ${t('tab.producers')} - ${t('header.tap')}`,
      14, THEME.text.gold, { fontWeight: 'bold' }
    );
    this.sectionTapLabel.x = pad;
    this.sectionTapLabel.y = y;
    content.addChild(this.sectionTapLabel);
    y += 28;

    for (const def of TAP_UPGRADES) {
      const row = this.createRow(def.id, 'tap', TAP_EMOJIS[def.id] || '\uD83D\uDC46', w, y);
      content.addChild(row.container);
      this.rows.push(row);
      y += LAYOUT.rowH + LAYOUT.gap;
    }

    y += 8;

    // Section: Producers
    this.sectionProdLabel = makeText(
      `\u2615 ${t('tab.producers')}`,
      14, THEME.text.gold, { fontWeight: 'bold' }
    );
    this.sectionProdLabel.x = pad;
    this.sectionProdLabel.y = y;
    content.addChild(this.sectionProdLabel);
    y += 28;

    for (const def of PRODUCERS) {
      const row = this.createRow(
        def.id, 'producer', PRODUCER_EMOJIS[def.id] || '\uD83C\uDF31', w, y
      );
      content.addChild(row.container);
      this.rows.push(row);
      y += LAYOUT.rowH + LAYOUT.gap;
    }

    y += pad;
    this.scroll.setContentHeight(y);
  }

  private createRow(
    id: string, kind: 'tap' | 'producer', emoji: string, totalW: number, y: number
  ): RowInfo {
    const container = new PIXI.Container();
    container.y = y;

    const pad = LAYOUT.padding;
    const rowW = totalW - pad * 2;
    const rowH = LAYOUT.rowH;

    // Card background
    const gfx = new PIXI.Graphics();
    drawGlassCard(gfx, pad, 0, rowW, rowH);
    container.addChild(gfx);

    // Icon
    const iconGfx = new PIXI.Graphics();
    drawIconBox(iconGfx, pad + 8, (rowH - LAYOUT.iconSize) / 2, LAYOUT.iconSize);
    container.addChild(iconGfx);

    const iconText = makeText(emoji, 22, THEME.text.white);
    iconText.anchor.set(0.5);
    iconText.x = pad + 8 + LAYOUT.iconSize / 2;
    iconText.y = rowH / 2;
    container.addChild(iconText);

    // Name
    const nameText = makeText('', 13, THEME.text.primary, { fontWeight: 'bold' });
    nameText.x = pad + 8 + LAYOUT.iconSize + 10;
    nameText.y = rowH / 2 - 12;
    container.addChild(nameText);

    // Info (level/owned + bps/power)
    const infoText = makeText('', 11, THEME.text.secondary);
    infoText.x = pad + 8 + LAYOUT.iconSize + 10;
    infoText.y = rowH / 2 + 6;
    container.addChild(infoText);

    // Buy button
    const btnW = 72;
    const btnH = 30;
    const btnX = pad + rowW - btnW - 8;
    const btnY = (rowH - btnH) / 2;

    const btnGfx = new PIXI.Graphics();
    btnGfx.eventMode = 'static';
    btnGfx.cursor = 'pointer';
    btnGfx.on('pointerdown', () => this.handleBuy(id, kind));
    container.addChild(btnGfx);

    const btnText = makeText('', 11, THEME.text.white, { fontWeight: 'bold' });
    btnText.anchor.set(0.5, 0.5);
    btnText.x = btnX + btnW / 2;
    btnText.y = btnY + btnH / 2;
    container.addChild(btnText);

    return { container, gfx, iconText, nameText, infoText, btnGfx, btnText, id, kind };
  }

  private handleBuy(id: string, kind: 'tap' | 'producer'): void {
    if (kind === 'tap') {
      this.core.dispatch({ type: 'BUY_TAP_UPGRADE', payload: { upgradeId: id } });
    } else {
      this.core.dispatch({ type: 'BUY_PRODUCER', payload: { producerId: id } });
    }
  }

  refresh(state: GameState): void {
    const pad = LAYOUT.padding;
    const rowW = this.panelW - pad * 2;
    const btnW = 72;
    const btnH = 30;

    for (const row of this.rows) {
      if (row.kind === 'tap') {
        const def = TAP_UPGRADES.find(d => d.id === row.id)!;
        const lvl = state.tapUpgrades[def.id] ?? 0;
        const cost = applyCostDiscount(state, getTapUpgradeCost(def, lvl));
        const canBuy = lvl < def.maxLevel && state.beans >= cost;
        const isMax = lvl >= def.maxLevel;

        row.nameText.text = t(def.nameKey);
        row.infoText.text = `${t('level')}${lvl}/${def.maxLevel}  +${formatNumber(def.tapPower)}/tap`;

        const btnX = pad + rowW - btnW - 8;
        const btnY = (LAYOUT.rowH - btnH) / 2;
        row.btnGfx.clear();
        const color = isMax ? THEME.button.disabled : canBuy ? THEME.button.primary : THEME.button.disabled;
        drawPillButton(row.btnGfx, btnX, btnY, btnW, btnH, color);

        row.btnText.text = isMax ? t('max') : formatNumber(cost);
        row.btnText.x = btnX + btnW / 2;
        row.btnText.y = btnY + btnH / 2;

        row.container.visible = true;
      } else {
        const def = PRODUCERS.find(d => d.id === row.id)!;
        const unlocked = isProducerUnlocked(def, state);
        row.container.visible = unlocked;

        if (!unlocked) continue;

        const owned = state.producers[def.id] ?? 0;
        const cost = applyCostDiscount(state, getProducerCost(def, owned), def.id);
        const canBuy = owned < def.maxCount && state.beans >= cost;
        const isMax = owned >= def.maxCount;

        row.nameText.text = t(def.nameKey);
        row.infoText.text = `${t('owned')}: ${owned}  ${formatNumber(def.baseBPS * (owned || 1))}/s`;

        const btnX = pad + rowW - btnW - 8;
        const btnY = (LAYOUT.rowH - btnH) / 2;
        row.btnGfx.clear();
        const color = isMax ? THEME.button.disabled : canBuy ? THEME.button.primary : THEME.button.disabled;
        drawPillButton(row.btnGfx, btnX, btnY, btnW, btnH, color);

        row.btnText.text = isMax ? t('max') : formatNumber(cost);
        row.btnText.x = btnX + btnW / 2;
        row.btnText.y = btnY + btnH / 2;
      }
    }

    // Recalculate content height based on visible rows
    this.recalcContentHeight(state);
  }

  private recalcContentHeight(_state: GameState): void {
    let y = LAYOUT.padding;
    // Tap section label
    this.sectionTapLabel.y = y;
    y += 28;

    for (const row of this.rows) {
      if (row.kind === 'tap') {
        row.container.y = y;
        y += LAYOUT.rowH + LAYOUT.gap;
      }
    }

    y += 8;
    this.sectionProdLabel.y = y;
    y += 28;

    for (const row of this.rows) {
      if (row.kind === 'producer' && row.container.visible) {
        row.container.y = y;
        y += LAYOUT.rowH + LAYOUT.gap;
      }
    }

    y += LAYOUT.padding;
    this.scroll.setContentHeight(y);
  }

  onResize(w: number, h: number): void {
    this.panelW = w;
    this.scroll.resize(w, h);
  }

  updateScroll(dt: number): void {
    this.scroll.update(dt);
  }
}
